import { h } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { sendMessage, loadSessionId, fetchChipsConfig } from './api.js'
import { readEE, currentPage, getUserState, isEEAllowed } from './config.js'
import { renderMarkdown } from './markdown.js'

export function Widget({ config }) {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [chips, setChips] = useState([])
  const [chipsMap, setChipsMap] = useState(null)
  const [allowed, setAllowed] = useState(null)
  const messagesEnd = useRef(null)

  const ee = readEE()
  const userState = getUserState(ee)
  const page = currentPage()
  const sessionId = loadSessionId(config.session_storage_key)

  useEffect(() => {
    isEEAllowed(ee, config).then(setAllowed)
  }, [])

  useEffect(() => {
    fetchChipsConfig(config.chips_config_url).then(setChipsMap)
  }, [])

  useEffect(() => {
    if (!chipsMap) return
    const stateKey = 'state_' + userState
    const forState = chipsMap[stateKey] || {}
    const pageChips = forState[page] || forState['_default'] || []
    setChips(pageChips.slice(0, 3))
  }, [chipsMap, userState, page])

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  async function submit(text) {
    const clean = (text || '').trim()
    if (!clean || loading) return
    setInput('')
    setMessages(m => [...m, { role: 'user', text: clean }])
    setLoading(true)
    try {
      const res = await sendMessage({
        config, ee, sessionId, userState, page,
        message: clean,
        chipsContext: []
      })
      setMessages(m => [...m, { role: 'ai', text: res.reply || '(sem resposta)' }])
      if (Array.isArray(res.suggested_chips)) setChips(res.suggested_chips.slice(0, 3))
    } catch (e) {
      setMessages(m => [...m, { role: 'err', text: 'Falhou o envio. Tente outra vez daqui a pouco.' }])
    } finally {
      setLoading(false)
    }
  }

  function onKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit(input)
    }
  }

  if (allowed !== true) return null

  return (
    <div class="wrap">
      {open && (
        <div class="panel" role="dialog" aria-label="Chat de apoio">
          <div class="header">
            <span>Precisa de ajuda?</span>
            <button onClick={() => setOpen(false)} aria-label="Fechar">×</button>
          </div>
          <div class="messages">
            {messages.length === 0 && (
              <div class="msg ai">Sou o NEXO, o assistente virtual da EDUGEP. Respondo sobre inscrições, pagamentos e serviços dos seus filhos.</div>
            )}
            {messages.map((m, i) => (
              m.role === 'ai'
                ? <div key={i} class="msg ai" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                : <div key={i} class={'msg ' + m.role}>{m.text}</div>
            ))}
            {loading && <div class="loading">A escrever…</div>}
            <div ref={messagesEnd} />
          </div>
          {chips.length > 0 && (
            <div class="chips">
              {chips.map((c, i) => (
                <button key={i} class="chip" onClick={() => submit(c)}>{c}</button>
              ))}
            </div>
          )}
          <div class="input">
            <textarea
              rows={2}
              placeholder="Escreva aqui a sua pergunta…"
              value={input}
              onInput={e => setInput(e.target.value)}
              onKeyDown={onKey}
              disabled={loading}
            />
            <button onClick={() => submit(input)} disabled={loading || !input.trim()}>Enviar</button>
          </div>
        </div>
      )}
      <button
        class={open ? 'bubble' : 'bubble bubble-label'}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Fechar chat' : 'Abrir chat de ajuda'}
        aria-expanded={open}
      >
        {open ? '×' : (
          <span class="bubble-inner">
            <span class="bubble-icon" aria-hidden="true">💬</span>
            <span class="bubble-text">Precisa de ajuda?</span>
          </span>
        )}
      </button>
    </div>
  )
}
